/**
 *
 * QResourcePicker
 *
 */

// BASE

import { chartOfAccountsSelectors, chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { customersSelectors } from 'app-components/flux/customers';
import { projectsSelectors } from 'app-components/flux/projects';
import { List as ImmutableList, Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import {
  CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE,
  QCS_ID_FOR_CLIENT_ONLY_UI_OPTION,
  VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION,
} from 'components/QFilterSelect/constants';
import { getCustomerIdOfSelectedProject } from 'components/QFilterSelect/utils';

import QButton from 'components/QButton';
import QTextButton from 'components/QTextButton';
// CUSTOM COMPONENTS
import ResourceRegister from 'components/ResourceRegister';

// MUI COMPONENTS
import Typography from '@mui/material/Typography';

import { getLogger } from 'app-components/utils/core';

// PATH RELATIVE IMPORTS
import {
  dataDictionary,
  getChildIds,
  makeClientsAndProjectsData,
  makeLocalCategoriesData,
  resourceConfig,
} from './utils';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
// STYLES HOOK
import { styles } from './styles';
const useStyles = makeStyles()(styles);

const logger = getLogger('QResourcePicker.js');

/*
 * QResourcePicker Component ***********************************
 */

// PROP TYPES
const propTypes = {
  resourceType: PropTypes.string,
  onSelect: PropTypes.func,
  pageSize: PropTypes.number,
  selectedItems: PropTypes.object,
  noHeader: PropTypes.bool,
  noSearch: PropTypes.bool,
  autoFocus: PropTypes.bool,
  filterFn: PropTypes.func,
  placeholder: PropTypes.string,
  children: PropTypes.object,
  noBorder: PropTypes.bool,
  noGoals: PropTypes.bool,
  noUncategorized: PropTypes.bool,
  selectAll: PropTypes.bool,
  noSpecial: PropTypes.bool,
};

const QResourcePicker = (props) => {
  // PROPS ============================================================================
  const {
    resourceType,
    onSelect,
    pageSize,
    selectedItems,
    noHeader,
    noSearch,
    autoFocus,
    filterFn,
    placeholder,
    noBorder,
    noGoals,
    noUncategorized,
    selectAll = false,
    noSpecial = false,
  } = props;

  const resourceSelector = resourceConfig[resourceType]?.selector;

  // STATE
  const resourcesById = useSelector((state) => resourceSelector(state), shallowEqual);
  const chartOfAccounts = useSelector((state) => chartOfAccountsSelectors.getChartOfAccountsTree(state), shallowEqual);
  const customersById = useSelector(customersSelectors.getCustomers);
  const projectsByCustomerId = useSelector((state) => projectsSelectors.getProjectsByCustomerId(state), shallowEqual);
  const businessEnabled = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'businessEnabled'));

  const [data, setData] = useState(ImmutableList());
  const [searchFilter, setSearchFilter] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [showMore, setShowMore] = useState(true);
  const [searchChildren, setSearchChildren] = useState(false);

  const resourceTitle = resourceType.charAt(0).toUpperCase() + resourceType.slice(1);

  // EFFECTS ============================================================================

  /*
   * Make Data
   */
  useEffect(() => {
    logger.log('qresource **QResourcePicker new data', resourceType, resourcesById);

    if (resourcesById) {
      let newList;
      if (resourceType === 'categories') {
        newList = makeLocalCategoriesData({
          categories: resourcesById,
          chartOfAccounts,
          filter: searchFilter || null,
          filterFn: filterFn || null,
        });
        if (noUncategorized || noSpecial) {
          newList = newList.filter(
            (item) =>
              !(noUncategorized && item.id === 'UNCATEGORIZED') &&
              !(noSpecial && (item.id === 'TRANSFER' || item.id === 'OTHER')),
          );
        }

        // add in Uncategorized category
        if (!noUncategorized) {
          newList = newList.push({
            name: 'Uncategorized',
            type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED,
            id: '0',
          });
        }
      } else if (resourceType === 'payeeNames') {
        newList = resourcesById.map((name) => ({ id: name.toLowerCase(), name })); // resourcesById is a list for payees
        newList = searchFilter
          ? newList.filter((item) => item.name.toLowerCase().indexOf(searchFilter.toLowerCase()) !== -1)
          : newList;
        newList = filterFn ? newList.filter(filterFn) : newList;
      } else if (resourceType === CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE) {
        newList = makeClientsAndProjectsData({
          customersById: resourcesById,
          projectsByCustomerId: projectsByCustomerId,
          searchFilter: searchFilter || null,
        });
      } else {
        newList = ImmutableList(
          resourcesById
            .sortBy(({ name }) => name?.toLowerCase())
            .toList()
            .toJS(),
        );
        if (searchFilter) {
          newList = newList.filter((item) => item.name.toLowerCase().indexOf(searchFilter.toLowerCase()) !== -1);
        }
        newList = filterFn ? newList.filter(filterFn) : newList;
      }

      /*  Filter out GOALS tag types from Data! */
      if (resourceType === 'tags' && noGoals) {
        newList = newList.filter((item) => (item?.type ? item.type !== 'GOAL' : true));
      }

      if (resourceType === 'accounts') {
        newList = filterFn ? newList.filter(filterFn) : newList;
      }

      if (pageSize) newList = newList.slice(0, pageSize * pageNum);

      setData(newList);
      setShowMore(newList.size >= pageSize * pageNum);

      if (resourceType === CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE) {
        setSearchChildren(true); // Expand all child nodes as default view
      } else {
        setSearchChildren(false);
      }
    }
  }, [
    resourceType,
    pageNum,
    resourcesById,
    chartOfAccounts,
    searchFilter,
    pageSize,
    filterFn,
    noGoals,
    noUncategorized,
    noSpecial,
    projectsByCustomerId,
    businessEnabled,
  ]);

  // STYLES ============================================================================
  const { classes } = useStyles(props);

  // INTERNAL FUNCTIONS ============================================================================

  /*
   * Handle Select
   */
  const handleSelect = useCallback(
    (item) => {
      let newSelectedItems = selectedItems;
      let childIds;

      const itemSelect = (it) => {
        if (it.id === 'BALANCE_ADJUSTMENT_2') {
          childIds = data
            .filter((acc) => acc.id === 'BALANCE_ADJUSTMENT_2')
            .get(0, { children: [] })
            .children.map((children) => children.id);
        } else {
          childIds = getChildIds(it);
        }
        if (newSelectedItems.includes(it.id)) {
          newSelectedItems = newSelectedItems.delete(it.id);
          if (childIds) newSelectedItems = newSelectedItems.subtract(childIds);
          /*
           For 'Clients & Projects' filter, when any child(Project) node gets unselected, then we
           need to enusre that we mark it's related parent node(client) as unselected.

          */
          if (resourceType === CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE) {
            if (!customersById.has(it.id)) {
              // This is a child node (Hence user has either unchecked project name or "Client only" UI option on Filter UI)
              // Check if parent (customer/client) node of this child node is in selected state
              const customerId = getCustomerIdOfSelectedProject(it.id);
              if (newSelectedItems.includes(customerId)) {
                // Mark (customer/client) node as unselected
                newSelectedItems = newSelectedItems.delete(customerId);
              }
            }
          }
        } else {
          newSelectedItems = newSelectedItems.add(it.id);
          if (resourceType === 'categories' || resourceType === CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE) {
            if (childIds) {
              // Mark all child nodes as selected when parent node is selected
              newSelectedItems = newSelectedItems.union(childIds);
            }
          }
          /*
           For 'Clients & Projects' filter, when all child(Project) nodes are selected, then we
           need to enusre that we mark it's parent node(client) as selected.
          */
          if (resourceType === CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE) {
            if (!customersById.has(it.id)) {
              // This is a child node (Hence user has either checked project name or "Client only" option on Filter UI)
              // Get Id of it's parent(client)
              const customerId = getCustomerIdOfSelectedProject(it.id);

              // Get all projects(children) of this client(parent)
              const allProjects = projectsByCustomerId.get(customerId)?.allProjects;
              let allChildrenSelected = true;
              if (allProjects?.size > 0) {
                for (const project of allProjects) {
                  // Check if we have any project that is not selected
                  if (!newSelectedItems.includes(project.id)) {
                    allChildrenSelected = false;
                  }
                }
              }
              // Check if "Client only" UI option is also checked
              const clientOnlyUIOptionId = `${customerId}${VALUE_DELIMITER_FOR_CLIENT_ONLY_UI_OPTION}${QCS_ID_FOR_CLIENT_ONLY_UI_OPTION}`;
              if (!newSelectedItems.includes(clientOnlyUIOptionId)) {
                allChildrenSelected = false;
              }
              if (allChildrenSelected) {
                // Mark parent(customer/client) node as selected as all of it's children are selected
                newSelectedItems = newSelectedItems.add(customerId);
              }
            }
          }
        }
      };

      if (ImmutableList.isList(item)) {
        if (selectedItems.size > 0) {
          newSelectedItems = new ImmutableSet();
        } else {
          item.forEach((i) => itemSelect(i));
        }
      } else {
        itemSelect(item);
      }

      if (onSelect) onSelect(newSelectedItems);
    },
    [selectedItems, resourceType, onSelect, data, customersById, projectsByCustomerId],
  );

  /*
   * Handle Search Change
   */
  const handleSearchChange = useCallback(
    (filter) => {
      setSearchFilter(filter);
      if (!searchChildren && filter) {
        setSearchChildren(true);
      } else if (filter === null && searchChildren) {
        setSearchChildren(false);
      }
    },
    [searchChildren],
  );

  const clearSelections = useCallback(() => {
    onSelect(ImmutableSet());
  }, [onSelect]);

  return (
    <div className={classes.resourcesContainer}>
      {!noHeader && (
        <div className={classes.header}>
          <Typography className={classes.title} variant='titleRegular'>
            {`Pick ${resourceTitle}`}
          </Typography>
          {selectedItems.size > 0 && (
            <QTextButton title='Clear Selected' onClick={clearSelections} classes={{ root: classes.actionText }} />
          )}
        </div>
      )}
      <div className={classes.pickerContainer}>
        <ResourceRegister
          autoFocus={autoFocus}
          classes={{ rootContainer: classes.listRoot }}
          dataDictionary={dataDictionary.filterNot((x) => x.hide)}
          data={data}
          onSelect={handleSelect}
          selectedItems={selectedItems}
          noHeader
          onSearchChange={noSearch ? null : handleSearchChange}
          resourceType={resourceType}
          narrowMode
          placeholder={placeholder}
          noBorder={noBorder}
          searchChildren={searchChildren}
          selectAll={selectAll}
        >
          {props.children}
        </ResourceRegister>
        {showMore && (
          <QButton
            id='show-more-button' //  Used in a JQuery which allows keyboard controls to be used
            onClick={() => setPageNum(pageNum + 1)}
          >
            {`Show More ${resourceTitle}`}
          </QButton>
        )}
      </div>
    </div>
  );
};

QResourcePicker.propTypes = propTypes;

export default QResourcePicker;
